import { EventAttendeesService } from "../services/eventAttendees.service"
import type { Attendee, IntroResult } from "../models/attendee.models"
import { i18n } from "../../internationalize/i18n.service"
import "../styles/eventAttendees.style.scss"

// Same copy as the mobile apps' intro sheet. The note reaches the TBW TEAM, who make
// the introduction, not the person: the label says so and the placeholder asks a
// question, because a member once wrote "Bonjour Gaia…" believing he was writing to her.
const INTRO_COPY = {
  en: {
    column: "Intro",
    button: "Request intro",
    title: "Request an introduction",
    reason: "Reason (optional)",
    reasons: ["Explore a partnership", "Compare notes / learnings", "Discuss an investment"],
    noteLabel: "Add a note for the TBW team (required)",
    notePlaceholder: "Why would you like an introduction to {name}?",
    disclaimer: "Requests are reviewed by the TBW team before they’re forwarded. No contact details are shared until accepted.",
    cancel: "Cancel",
    send: "Send request",
    sending: "Sending…",
    sentTitle: "Request sent",
    sentBody: "Your request to {name} is with the TBW team. We’ll get back to you.",
    close: "Close",
    forbidden: "Introduction requests are reserved for TBW members.",
    rateLimited: "You’ve sent several requests in a short time. Please try again in an hour.",
    error: "Couldn’t send your request. Please try again.",
  },
  fr: {
    column: "Intro",
    button: "Demander une intro",
    title: "Demander une introduction",
    reason: "Motif (facultatif)",
    reasons: ["Explorer un partenariat", "Échanger des retours d’expérience", "Discuter d’un investissement"],
    noteLabel: "Ajouter une note pour l’équipe TBW (obligatoire)",
    notePlaceholder: "Pourquoi souhaitez-vous être mis en relation avec {name} ?",
    disclaimer: "Les demandes sont examinées par l’équipe TBW avant transmission. Aucune coordonnée n’est partagée avant acceptation.",
    cancel: "Annuler",
    send: "Envoyer la demande",
    sending: "Envoi…",
    sentTitle: "Demande envoyée",
    sentBody: "Votre demande auprès de {name} a bien été transmise à l’équipe TBW, qui reviendra vers vous.",
    close: "Fermer",
    forbidden: "Les demandes d’introduction sont réservées aux membres TBW.",
    rateLimited: "Vous avez envoyé plusieurs demandes en peu de temps. Réessayez dans une heure.",
    error: "Impossible d’envoyer votre demande. Réessayez.",
  },
}

/** Escape a string for safe interpolation into innerHTML (text + attributes). */
function escapeHtml(value: string): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

/** Only allow http(s) links; otherwise return empty to avoid javascript: URLs. */
function safeHref(url: string): string {
  const v = (url ?? "").trim()
  if (/^https?:\/\//i.test(v)) return v
  return ""
}

/**
 * <tbw-event-attendees data-event-slug="hosnights-april">
 *
 * Renders the checked-in attendee directory for a single event, with a live
 * client-side search, a CSV download button and, on each row, an introduction
 * request (POST /intro-requests, as in the mobile apps). Data comes from the
 * backend GET /events/:slug/attendees (auth-gated).
 */
export class EventAttendeesComponent extends HTMLElement {
  private attendees: Attendee[] = []
  private filtered: Attendee[] = []
  private slug = ""
  private query = ""
  // Not `lang`: HTMLElement already has a (public) `lang` attribute.
  private readonly introLang = i18n.getLanguage() === "fr" ? "fr" : "en"
  private readonly copy = INTRO_COPY[this.introLang]
  private introTarget: Attendee | null = null

  constructor(
    private service: EventAttendeesService = window.tbwServices.eventAttendeesService
  ) {
    super()
    this.classList.add("tbw-event-attendees")
  }

  connectedCallback() {
    this.slug = this.dataset.eventSlug?.trim() ?? ""
    if (!this.slug) {
      console.warn("[tbw-event-attendees] missing data-event-slug attribute")
      return
    }
    this.renderLoading()
    void this.load()
  }

  private async load() {
    const result = await this.service.getAttendees(this.slug)
    if (result.status === "forbidden") {
      this.renderRestricted()
      return
    }
    if (result.status === "error") {
      this.renderError()
      return
    }
    this.attendees = result.attendees
    this.filtered = this.attendees
    this.render()
  }

  private applyFilter() {
    const q = this.query.toLowerCase().trim()
    if (!q) {
      this.filtered = this.attendees
      return
    }
    this.filtered = this.attendees.filter((a) =>
      `${a.name} ${a.company} ${a.jobTitle}`.toLowerCase().includes(q)
    )
  }

  private renderLoading() {
    this.innerHTML = `<div class="tbw-ea__state">Loading attendees…</div>`
  }

  private renderRestricted() {
    this.innerHTML = `
      <div class="tbw-ea__state tbw-ea__state--restricted">
        <strong>Attendee list restricted</strong>
        <p>This directory is only available to people who attended this event.</p>
      </div>`
  }

  private renderError() {
    this.innerHTML = `
      <div class="tbw-ea__state">
        Couldn't load the attendee list. Please try again later.
      </div>`
  }

  private render() {
    if (this.attendees.length === 0) {
      this.innerHTML = `<div class="tbw-ea__state">No attendees to show yet.</div>`
      return
    }

    this.innerHTML = `
      <div class="tbw-ea__header">
        <div class="tbw-ea__count">Attendees <span>(${this.attendees.length})</span></div>
        <button class="tbw-ea__download" type="button">Download CSV</button>
      </div>
      <input class="tbw-ea__search" type="search" placeholder="Search by name, company, or job title…" value="${escapeHtml(this.query)}" />
      <div class="tbw-ea__scroll">
        <table class="tbw-ea__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Job Title</th>
              <th>Company</th>
              <th class="tbw-ea__th-linkedin">LinkedIn</th>
              <th class="tbw-ea__th-intro">${escapeHtml(this.copy.column)}</th>
            </tr>
          </thead>
          <tbody>${this.renderRows()}</tbody>
        </table>
      </div>
      ${this.renderIntroDialog()}
    `

    const search = this.querySelector<HTMLInputElement>(".tbw-ea__search")
    search?.addEventListener("input", this.onSearch)
    // One listener on the body: rows are re-rendered on every search keystroke.
    this.querySelector("tbody")?.addEventListener("click", this.onRowClick)
    this.bindIntroDialog()
    // keep focus + caret after re-render
    if (this.query && search) {
      search.focus()
      search.setSelectionRange(search.value.length, search.value.length)
    }

    this.querySelector<HTMLButtonElement>(".tbw-ea__download")
      ?.addEventListener("click", this.onDownload)
  }

  private renderRows(): string {
    if (this.filtered.length === 0) {
      return `<tr><td colspan="5" class="tbw-ea__empty">No match for "${escapeHtml(this.query)}"</td></tr>`
    }
    return this.filtered
      .map((a, index) => {
        const avatar = safeHref(a.avatarUrl)
        // Only render an <img> when Luma actually returned a photo. No initial
        // fallback — empty space is cleaner than a generic letter badge.
        const avatarHtml = avatar
          ? `<img class="tbw-ea__avatar" src="${escapeHtml(avatar)}" alt="${escapeHtml(a.name)}" loading="lazy" decoding="async" />`
          : ""
        const linkedin = safeHref(a.linkedin)
        const linkedinHtml = linkedin
          ? `<a class="tbw-ea__linkedin" href="${escapeHtml(linkedin)}" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn profile of ${escapeHtml(a.name)}">in</a>`
          : ""
        return `
          <tr>
            <td class="tbw-ea__name-cell">
              ${avatarHtml}
              <span class="tbw-ea__name">${escapeHtml(a.name)}</span>
            </td>
            <td>${escapeHtml(a.jobTitle)}</td>
            <td>${escapeHtml(a.company)}</td>
            <td class="tbw-ea__td-linkedin">${linkedinHtml}</td>
            <td class="tbw-ea__td-intro">
              <button class="tbw-ea__intro" type="button" data-index="${index}">${escapeHtml(this.copy.button)}</button>
            </td>
          </tr>`
      })
      .join("")
  }

  private renderIntroDialog(): string {
    const c = this.copy
    const reasons = c.reasons
      .map((r) => `<label class="tbw-ea__reason"><input type="radio" name="tbw-ea-reason" value="${escapeHtml(r)}" /><span>${escapeHtml(r)}</span></label>`)
      .join("")
    return `
      <dialog class="tbw-ea__dialog">
        <form class="tbw-ea__form" method="dialog">
          <h3 class="tbw-ea__dialog-title">${escapeHtml(c.title)}</h3>
          <p class="tbw-ea__dialog-target"></p>
          <fieldset class="tbw-ea__reasons">
            <legend>${escapeHtml(c.reason)}</legend>
            ${reasons}
          </fieldset>
          <label class="tbw-ea__note-label" for="tbw-ea-note">${escapeHtml(c.noteLabel)}</label>
          <textarea id="tbw-ea-note" class="tbw-ea__note" rows="4" maxlength="1000"></textarea>
          <p class="tbw-ea__disclaimer">${escapeHtml(c.disclaimer)}</p>
          <p class="tbw-ea__dialog-error" role="alert" hidden></p>
          <div class="tbw-ea__actions">
            <button class="tbw-ea__cancel" type="button">${escapeHtml(c.cancel)}</button>
            <button class="tbw-ea__send" type="button" disabled>${escapeHtml(c.send)}</button>
          </div>
        </form>
        <div class="tbw-ea__sent" hidden>
          <h3 class="tbw-ea__dialog-title">${escapeHtml(c.sentTitle)}</h3>
          <p class="tbw-ea__sent-body"></p>
          <div class="tbw-ea__actions">
            <button class="tbw-ea__close" type="button">${escapeHtml(c.close)}</button>
          </div>
        </div>
      </dialog>`
  }

  private bindIntroDialog() {
    const dialog = this.querySelector<HTMLDialogElement>(".tbw-ea__dialog")
    if (!dialog) return
    const note = dialog.querySelector<HTMLTextAreaElement>(".tbw-ea__note")!
    const send = dialog.querySelector<HTMLButtonElement>(".tbw-ea__send")!
    note.addEventListener("input", () => { send.disabled = !note.value.trim() })
    send.addEventListener("click", this.onSendIntro)
    dialog.querySelector(".tbw-ea__cancel")?.addEventListener("click", () => dialog.close())
    dialog.querySelector(".tbw-ea__close")?.addEventListener("click", () => dialog.close())
    // A click on the backdrop (outside the form) closes it too.
    dialog.addEventListener("click", (e) => { if (e.target === dialog) dialog.close() })
  }

  private onRowClick = (e: Event) => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>(".tbw-ea__intro")
    if (!button) return
    const attendee = this.filtered[Number(button.dataset.index)]
    if (attendee) this.openIntro(attendee)
  }

  private openIntro(attendee: Attendee) {
    const dialog = this.querySelector<HTMLDialogElement>(".tbw-ea__dialog")
    if (!dialog) return
    this.introTarget = attendee
    const who = attendee.company ? `${attendee.name} · ${attendee.company}` : attendee.name
    dialog.querySelector(".tbw-ea__dialog-target")!.textContent = who
    const note = dialog.querySelector<HTMLTextAreaElement>(".tbw-ea__note")!
    note.value = ""
    note.placeholder = this.copy.notePlaceholder.replace("{name}", attendee.name)
    dialog.querySelectorAll<HTMLInputElement>("input[name=tbw-ea-reason]").forEach((r) => { r.checked = false })
    const send = dialog.querySelector<HTMLButtonElement>(".tbw-ea__send")!
    send.disabled = true
    send.textContent = this.copy.send
    this.showIntroError(null)
    dialog.querySelector<HTMLElement>(".tbw-ea__form")!.hidden = false
    dialog.querySelector<HTMLElement>(".tbw-ea__sent")!.hidden = true
    dialog.showModal()
    note.focus()
  }

  private showIntroError(message: string | null) {
    const el = this.querySelector<HTMLElement>(".tbw-ea__dialog-error")
    if (!el) return
    el.textContent = message ?? ""
    el.hidden = !message
  }

  private onSendIntro = async () => {
    const dialog = this.querySelector<HTMLDialogElement>(".tbw-ea__dialog")
    const target = this.introTarget
    if (!dialog || !target) return
    const note = dialog.querySelector<HTMLTextAreaElement>(".tbw-ea__note")!.value.trim()
    if (!note) return
    const reason = dialog.querySelector<HTMLInputElement>("input[name=tbw-ea-reason]:checked")?.value
    const send = dialog.querySelector<HTMLButtonElement>(".tbw-ea__send")!
    send.disabled = true
    send.textContent = this.copy.sending
    this.showIntroError(null)

    // The company goes with the name: the team reads the request in the CRM without
    // the event page in front of them, and a name alone is often ambiguous.
    const targetName = target.company ? `${target.name} (${target.company})` : target.name
    const result: IntroResult = await this.service.requestIntro({ targetName, reason, note }, this.introLang)

    if (result === "sent") {
      dialog.querySelector(".tbw-ea__sent-body")!.textContent = this.copy.sentBody.replace("{name}", target.name)
      dialog.querySelector<HTMLElement>(".tbw-ea__form")!.hidden = true
      dialog.querySelector<HTMLElement>(".tbw-ea__sent")!.hidden = false
      return
    }
    send.textContent = this.copy.send
    send.disabled = false
    this.showIntroError(
      result === "forbidden" ? this.copy.forbidden : result === "rate_limited" ? this.copy.rateLimited : this.copy.error,
    )
  }

  private onSearch = (e: Event) => {
    this.query = (e.target as HTMLInputElement).value
    this.applyFilter()
    // only re-render the tbody to keep the input mounted
    const tbody = this.querySelector("tbody")
    if (tbody) tbody.innerHTML = this.renderRows()
  }

  private onDownload = async () => {
    const blob = await this.service.getAttendeesCsvBlob(this.slug)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${this.slug}-attendees.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  disconnectedCallback() {
    this.querySelector(".tbw-ea__search")?.removeEventListener("input", this.onSearch)
    this.querySelector(".tbw-ea__download")?.removeEventListener("click", this.onDownload)
    this.querySelector("tbody")?.removeEventListener("click", this.onRowClick)
  }
}
